import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faSave, 
  faTimes, 
  faCheck,
  faExclamationTriangle,
  faSpinner,
  faCog,
  faGraduationCap,
  faList,
  faBrain,
  faSearch,
  faSliders
} from '@fortawesome/free-solid-svg-icons';
import { getApiUrl, SERVERS, ENDPOINTS } from '../config/api';
import CustomFieldsManagement from '../components/CustomFieldsManagement';
import './RubricManagement.css';

const RubricManagement = () => {
    const [activeTab, setActiveTab] = useState('rubrics'); // 'rubrics' or 'custom-fields'
    const [rubrics, setRubrics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [editingRubric, setEditingRubric] = useState(null);
    const [editingCriterion, setEditingCriterion] = useState(null);
    const [showCriterionForm, setShowCriterionForm] = useState(false);
    const [showBloomForm, setShowBloomForm] = useState(false);
    const [rubricTypeFilter, setRubricTypeFilter] = useState('all'); // 'all', 'question', 'paper'

    // Form states
    const [rubricForm, setRubricForm] = useState({
        name: '',
        description: '',
        rubric_type: 'question',
        is_generic: false,
        is_active: true
    });

    const [criterionForm, setCriterionForm] = useState({
        criterion_name: '',
        score_descriptions: {
            '0': '',
            '1': '',
            '2': '',
            '3': ''
        },
        scale_min: 0,
        scale_max: 3
    });

    const [bloomForm, setBloomForm] = useState({
        level_descriptions: {
            '1': '',
            '2': '',
            '3': '',
            '4': '',
            '5': '',
            '6': ''
        }
    });

    // fetchRubrics is un-memoized but reads exactly rubricTypeFilter, already
    // the sole dep below.
    useEffect(() => {
        fetchRubrics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rubricTypeFilter]);

    const fetchRubrics = async () => {
        try {
            setLoading(true);
            let url = getApiUrl(SERVERS.DATABASE, ENDPOINTS.RUBRICS) + '?include_inactive=true';
            if (rubricTypeFilter !== 'all') {
                url += `&rubric_type=${rubricTypeFilter}`;
            }
            const response = await fetch(url);
            const data = await response.json();
            
            if (data.success) {
                setRubrics(data.data);
            } else {
                setError('Failed to fetch rubrics');
            }
        } catch (err) {
            setError('Error fetching rubrics: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateRubric = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(getApiUrl(SERVERS.DATABASE, ENDPOINTS.RUBRICS), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(rubricForm)
            });

            const data = await response.json();
            if (data.success) {
                setShowCreateForm(false);
                setRubricForm({ name: '', description: '', rubric_type: 'question', is_generic: false, is_active: true });
                fetchRubrics();
                setError(null); // Clear any previous errors
            } else {
                setError(data.detail || 'Failed to create rubric');
            }
        } catch (err) {
            setError('Network error: Unable to connect to server. Please try again.');
        }
    };

    const handleUpdateRubric = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${getApiUrl(SERVERS.DATABASE, ENDPOINTS.RUBRIC)}/${editingRubric.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(rubricForm)
            });

            const data = await response.json();
            if (data.success) {
                setShowEditForm(false);
                setEditingRubric(null);
                setRubricForm({ name: '', description: '', rubric_type: 'question', is_generic: false, is_active: true });
                fetchRubrics();
            } else {
                setError('Failed to update rubric: ' + data.detail);
            }
        } catch (err) {
            setError('Error updating rubric: ' + err.message);
        }
    };

    const handleDeleteRubric = async (rubricId, isGeneric) => {
        if (isGeneric) {
            setError('Cannot delete generic rubric');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this rubric?')) {
            return;
        }

        try {
            const response = await fetch(`${getApiUrl(SERVERS.DATABASE, ENDPOINTS.RUBRIC)}/${rubricId}`, {
                method: 'DELETE'
            });

            const data = await response.json();
            if (data.success) {
                fetchRubrics();
            } else {
                setError('Failed to delete rubric: ' + data.detail);
            }
        } catch (err) {
            setError('Error deleting rubric: ' + err.message);
        }
    };

    const handleAddCriterion = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${getApiUrl(SERVERS.DATABASE, ENDPOINTS.RUBRIC_CRITERIA)}/${editingRubric.id}/criteria`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({...criterionForm, is_required: true})
            });

            const data = await response.json();
            if (data.success) {
                setShowCriterionForm(false);
                setCriterionForm({
                    criterion_name: '',
                    score_descriptions: {
                        '0': '',
                        '1': '',
                        '2': '',
                        '3': ''
                    },
                    scale_min: 0,
                    scale_max: 3
                });
                fetchRubrics();
            } else {
                setError('Failed to add criterion: ' + data.detail);
            }
        } catch (err) {
            setError('Error adding criterion: ' + err.message);
        }
    };

    const handleUpdateCriterion = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${getApiUrl(SERVERS.DATABASE, ENDPOINTS.RUBRIC_CRITERIA)}/criteria/${editingCriterion.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({...criterionForm, is_required: true})
            });

            const data = await response.json();
            if (data.success) {
                setShowCriterionForm(false);
                setEditingCriterion(null);
                setCriterionForm({
                    criterion_name: '',
                    score_descriptions: {
                        '0': '',
                        '1': '',
                        '2': '',
                        '3': ''
                    },
                    scale_min: 0,
                    scale_max: 3
                });
                fetchRubrics();
            } else {
                setError('Failed to update criterion: ' + data.detail);
            }
        } catch (err) {
            setError('Error updating criterion: ' + err.message);
        }
    };

    const handleDeleteCriterion = async (criterionId) => {
        if (!window.confirm('Are you sure you want to delete this criterion?')) {
            return;
        }

        try {
            const response = await fetch(`${getApiUrl(SERVERS.DATABASE, ENDPOINTS.RUBRIC_CRITERIA)}/criteria/${criterionId}`, {
                method: 'DELETE'
            });

            const data = await response.json();
            if (data.success) {
                fetchRubrics();
            } else {
                setError('Failed to delete criterion: ' + data.detail);
            }
        } catch (err) {
            setError('Error deleting criterion: ' + err.message);
        }
    };

    const handleSetBloomTaxonomy = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${getApiUrl(SERVERS.DATABASE, ENDPOINTS.RUBRIC_BLOOM_TAXONOMY)}/${editingRubric.id}/bloom-taxonomy`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(bloomForm)
            });

            const data = await response.json();
            if (data.success) {
                setShowBloomForm(false);
                setBloomForm({
                    level_descriptions: {
                        '1': '',
                        '2': '',
                        '3': '',
                        '4': '',
                        '5': '',
                        '6': ''
                    }
                });
                fetchRubrics();
            } else {
                setError('Failed to set Bloom\'s taxonomy: ' + data.detail);
            }
        } catch (err) {
            setError('Error setting Bloom\'s taxonomy: ' + err.message);
        }
    };

    const handleValidateRubric = async (rubricId) => {
        try {
            const response = await fetch(`${getApiUrl(SERVERS.DATABASE, ENDPOINTS.RUBRIC_VALIDATE)}/${rubricId}/validate`, {
                method: 'POST'
            });

            const data = await response.json();
            if (data.success) {
                if (data.data.is_valid) {
                    alert('Rubric is valid and complete!');
                } else {
                    alert('Rubric validation failed:\n' + data.data.errors.join('\n'));
                }
            } else {
                setError('Failed to validate rubric: ' + data.detail);
            }
        } catch (err) {
            setError('Error validating rubric: ' + err.message);
        }
    };

    const openEditForm = (rubric) => {
        setEditingRubric(rubric);
        setRubricForm({
            name: rubric.name,
            description: rubric.description || '',
            rubric_type: rubric.rubric_type || 'question',
            is_generic: rubric.is_generic,
            is_active: rubric.is_active
        });
        setShowEditForm(true);
    };

    const openCriterionForm = (rubric, criterion = null) => {
        setEditingRubric(rubric);
        setEditingCriterion(criterion);
        if (criterion) {
            setCriterionForm({
                criterion_name: criterion.criterion_name,
                score_descriptions: criterion.score_descriptions || {
                    '0': '', '1': '', '2': '', '3': ''
                },
                scale_min: criterion.scale_min ?? 0,
                scale_max: criterion.scale_max ?? 3,
            });
        } else {
            setCriterionForm({
                criterion_name: '',
                score_descriptions: { '0': '', '1': '', '2': '', '3': ''},
                scale_min: 0,
                scale_max: 3,
                is_required: true
            });
        }
        setShowCriterionForm(true);
    };

    const openBloomForm = (rubric) => {
        setEditingRubric(rubric);
        if (rubric.bloom_taxonomy && rubric.bloom_taxonomy.level_descriptions) {
            setBloomForm({
                level_descriptions: rubric.bloom_taxonomy.level_descriptions
            });
        } else {
            setBloomForm({
                level_descriptions: {
                    '1': '',
                    '2': '',
                    '3': '',
                    '4': '',
                    '5': '',
                    '6': ''
                }
            });
        }
        setShowBloomForm(true);
    };

    if (loading) {
        return (
            <div className="rubric-management-container">
                <div className="container">
                    <div className="loading">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Loading rubrics...
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="rubric-management-container">
            <div className="container">
                <div className="rubric-header">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faCog} />
                    </div>
                    <div className="header-text">
                        <h1>Settings & Configuration</h1>
                        <p>Manage rubrics, custom fields, and system configuration</p>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="tab-navigation">
                    <button
                        className={`tab-button ${activeTab === 'rubrics' ? 'active' : ''}`}
                        onClick={() => setActiveTab('rubrics')}
                    >
                        <FontAwesomeIcon icon={faGraduationCap} />
                        Rubrics Management
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'custom-fields' ? 'active' : ''}`}
                        onClick={() => setActiveTab('custom-fields')}
                    >
                        <FontAwesomeIcon icon={faSliders} />
                        Custom Fields
                    </button>
                </div>

                {/* Tab Content */}
                {activeTab === 'custom-fields' ? (
                    <CustomFieldsManagement />
                ) : (
                <>
                {/* Original Rubrics Management Content */}
                <div className="rubric-filters">
                    <div className="filter-group">
                        <label htmlFor="rubric-type-filter">Filter by Type:</label>
                        <select 
                            id="rubric-type-filter"
                            value={rubricTypeFilter} 
                            onChange={(e) => setRubricTypeFilter(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">All Rubrics</option>
                            <option value="question">Question Level</option>
                            <option value="paper">Question Paper Level</option>
                        </select>
                    </div>
                </div>

                <div className="rubric-actions">
                    <button 
                        className="btn btn-primary"
                        onClick={() => setShowCreateForm(true)}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        Create New Rubric
                    </button>
                </div>

                {error && (
                    <div className="error-message">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {error}
                        <button onClick={() => setError(null)}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                )}

                <div className="rubrics-list">
                {rubrics.map(rubric => (
                    <div key={rubric.id} className="rubric-card">
                        <div className="rubric-card-header">
                            <div className="rubric-title">
                                <h3>
                                    <FontAwesomeIcon icon={faGraduationCap} />
                                    {rubric.name}
                                </h3>
                                <div className="rubric-badges">
                                    <span className={`badge badge-${rubric.rubric_type || 'question'}`}>
                                        {rubric.rubric_type === 'paper' ? 'Paper Level' : 'Question Level'}
                                    </span>
                                    {rubric.is_generic && <span className="badge badge-generic">Generic</span>}
                                    {!rubric.is_active && <span className="badge badge-inactive">Inactive</span>}
                                </div>
                            </div>
                            <div className="rubric-actions">
                                <button 
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => handleValidateRubric(rubric.id)}
                                    title="Validate Rubric"
                                >
                                    <FontAwesomeIcon icon={faCheck} />
                                    Validate
                                </button>
                                <button 
                                    className="btn btn-sm btn-primary"
                                    onClick={() => openEditForm(rubric)}
                                    title="Edit Rubric"
                                >
                                    <FontAwesomeIcon icon={faEdit} />
                                    Edit
                                </button>
                                <button 
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDeleteRubric(rubric.id, rubric.is_generic)}
                                    disabled={rubric.is_generic}
                                    title={rubric.is_generic ? "Cannot delete generic rubric" : "Delete Rubric"}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                    Delete
                                </button>
                            </div>
                        </div>

                        {rubric.description && (
                            <p className="rubric-description">{rubric.description}</p>
                        )}

                        <div className="rubric-sections">
                            {/* Only show Bloom's Taxonomy for question level rubrics */}
                            {rubric.rubric_type === 'question' && (
                                <div className="section">
                                    <div className="section-header">
                                        <h4>
                                            <FontAwesomeIcon icon={faBrain} />
                                            Bloom's Taxonomy
                                        </h4>
                                        <button 
                                            className="btn btn-sm btn-outline"
                                            onClick={() => openBloomForm(rubric)}
                                            title={rubric.bloom_taxonomy ? "Edit Bloom's Taxonomy" : "Add Bloom's Taxonomy"}
                                        >
                                            <FontAwesomeIcon icon={rubric.bloom_taxonomy ? faEdit : faPlus} />
                                            {rubric.bloom_taxonomy ? 'Edit' : 'Add'}
                                        </button>
                                    </div>
                                    {rubric.bloom_taxonomy ? (
                                        <div className="bloom-config">
                                            <p><strong>Level Descriptions:</strong></p>
                                            <div className="level-descriptions">
                                                {Object.entries(rubric.bloom_taxonomy.level_descriptions || {}).map(([level, description]) => (
                                                    <div key={level} className="level-item">
                                                        <strong>Level {level}:</strong> {description}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="missing">Bloom's taxonomy not configured</p>
                                    )}
                                </div>
                            )}

                            <div className="section">
                                <div className="section-header">
                                    <h4>
                                        <FontAwesomeIcon icon={faList} />
                                        Criteria ({rubric.criteria.length})
                                    </h4>
                                    <button 
                                        className="btn btn-sm btn-outline"
                                        onClick={() => openCriterionForm(rubric)}
                                        title="Add New Criterion"
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                        Add Criterion
                                    </button>
                                </div>
                                {rubric.criteria.length > 0 ? (
                                    <div className="criteria-list">
                                        {rubric.criteria.map(criterion => (
                                            <div key={criterion.id} className="criterion-item">
                                                <div className="criterion-info">
                                                    <strong>{criterion.criterion_name}</strong>
                                                    <span className="scale">({(criterion.scale_min ?? 0)}-{(criterion.scale_max ?? 3)})</span>
                                                </div>
                                                <div className="criterion-actions">
                                                    <button 
                                                        className="btn btn-sm btn-outline"
                                                        onClick={() => openCriterionForm(rubric, criterion)}
                                                        title="Edit Criterion"
                                                    >
                                                        <FontAwesomeIcon icon={faEdit} />
                                                        Edit
                                                    </button>
                                                    <button 
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleDeleteCriterion(criterion.id)}
                                                        title="Delete Criterion"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="missing">No criteria defined</p>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
                </div>

                {/* Create Rubric Modal */}
            {showCreateForm && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>
                                <FontAwesomeIcon icon={faPlus} />
                                Create New Rubric
                            </h2>
                            <button onClick={() => setShowCreateForm(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleCreateRubric} className="modal-body">
                            <div className="form-group">
                                <label>Name *</label>
                                <input
                                    type="text"
                                    value={rubricForm.name}
                                    onChange={(e) => {
                                        setRubricForm({...rubricForm, name: e.target.value});
                                        if (error) setError(null); // Clear error when user starts typing
                                    }}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={rubricForm.description}
                                    onChange={(e) => setRubricForm({...rubricForm, description: e.target.value})}
                                />
                            </div>
                            <div className="form-group">
                                <label>Rubric Type *</label>
                                <select
                                    value={rubricForm.rubric_type}
                                    onChange={(e) => setRubricForm({...rubricForm, rubric_type: e.target.value})}
                                    required
                                >
                                    <option value="question">Question Level</option>
                                    <option value="paper">Question Paper Level</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={rubricForm.is_generic}
                                        onChange={(e) => setRubricForm({...rubricForm, is_generic: e.target.checked})}
                                    />
                                    Generic Rubric
                                </label>
                            </div>
                            <div className="form-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={rubricForm.is_active}
                                        onChange={(e) => setRubricForm({...rubricForm, is_active: e.target.checked})}
                                    />
                                    Active
                                </label>
                            </div>
                            <div className="modal-actions">
                                <button type="button" onClick={() => setShowCreateForm(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary">Create</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Rubric Modal */}
            {showEditForm && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>
                                <FontAwesomeIcon icon={faEdit} />
                                Edit Rubric
                            </h2>
                            <button onClick={() => setShowEditForm(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateRubric} className="modal-body">
                            <div className="form-group">
                                <label>Name *</label>
                                <input
                                    type="text"
                                    value={rubricForm.name}
                                    onChange={(e) => setRubricForm({...rubricForm, name: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={rubricForm.description}
                                    onChange={(e) => setRubricForm({...rubricForm, description: e.target.value})}
                                />
                            </div>
                            <div className="form-group">
                                <label>Rubric Type *</label>
                                <select
                                    value={rubricForm.rubric_type}
                                    onChange={(e) => setRubricForm({...rubricForm, rubric_type: e.target.value})}
                                    required
                                >
                                    <option value="question">Question Level</option>
                                    <option value="paper">Question Paper Level</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={rubricForm.is_active}
                                        onChange={(e) => setRubricForm({...rubricForm, is_active: e.target.checked})}
                                    />
                                    Active
                                </label>
                            </div>
                            <div className="modal-actions">
                                <button type="button" onClick={() => setShowEditForm(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary">Update</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Criterion Modal */}
            {showCriterionForm && (
                <div className="modal-overlay">
                    <div className="modal large">
                        <div className="modal-header">
                            <h2>
                                <FontAwesomeIcon icon={editingCriterion ? faEdit : faPlus} />
                                {editingCriterion ? 'Edit Criterion' : 'Add Criterion'}
                            </h2>
                            <button onClick={() => setShowCriterionForm(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={editingCriterion ? handleUpdateCriterion : handleAddCriterion} className="modal-body">
                            <div className="form-group">
                                <label>Criterion Name *</label>
                                <input
                                    type="text"
                                    value={criterionForm.criterion_name}
                                    onChange={(e) => setCriterionForm({...criterionForm, criterion_name: e.target.value})}
                                    required
                                    placeholder="e.g., clarity, complexity, relevance"
                                />
                            </div>
                            
                            <div className="form-help">
                                <strong>Score Descriptions (0-3):</strong><br/>
                                Define what each score level means for this criterion. The system will automatically generate appropriate prompts using these descriptions.
                            </div>
                            
                            <div className="score-descriptions">
                                {[0, 1, 2, 3].map(score => (
                                    <div key={score} className="form-group">
                                        <label>Score {score} Description *</label>
                                        <input
                                            type="text"
                                            value={criterionForm.score_descriptions[score.toString()]}
                                            onChange={(e) => setCriterionForm({
                                                ...criterionForm,
                                                score_descriptions: {
                                                    ...criterionForm.score_descriptions,
                                                    [score.toString()]: e.target.value
                                                }
                                            })}
                                            required
                                            placeholder={`e.g., ${score === 0 ? 'Not Acceptable' : 
                                                       score === 1 ? 'Partially Acceptable' :
                                                       score === 2 ? 'Acceptable' :
                                                       'Strong'}`}
                                        />
                                    </div>
                                ))}
                            </div>
                            
                            <div className="modal-actions">
                                <button type="button" onClick={() => setShowCriterionForm(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary">
                                    {editingCriterion ? 'Update' : 'Add'} Criterion
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Bloom's Taxonomy Modal */}
            {showBloomForm && (
                <div className="modal-overlay">
                    <div className="modal large">
                        <div className="modal-header">
                            <h2>
                                <FontAwesomeIcon icon={faBrain} />
                                Bloom's Taxonomy Configuration
                            </h2>
                            <button onClick={() => setShowBloomForm(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleSetBloomTaxonomy} className="modal-body">
                            <div className="form-group">
                                <p className="form-help">
                                    Define the descriptions for each Bloom's taxonomy level. These will be used in the analysis prompts.
                                </p>
                            </div>
                            
                            {Object.entries(bloomForm.level_descriptions).map(([level, description]) => (
                                <div key={level} className="form-group">
                                    <label>Level {level} Description *</label>
                                    <input
                                        type="text"
                                        value={description}
                                        onChange={(e) => setBloomForm({
                                            ...bloomForm,
                                            level_descriptions: {
                                                ...bloomForm.level_descriptions,
                                                [level]: e.target.value
                                            }
                                        })}
                                        required
                                        placeholder={`Enter description for Bloom's level ${level}`}
                                    />
                                </div>
                            ))}
                            
                            <div className="modal-actions">
                                <button type="button" onClick={() => setShowBloomForm(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary">Save Configuration</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            </>
            )}
            </div>
        </div>
    );
};

export default RubricManagement;
